import "virtual:windi-base.css";
import "virtual:windi-components.css";
import "virtual:windi-utilities.css";
import "virtual:windi-devtools";
import * as Sentry from "@sentry/vue";
import { VueQueryPlugin } from "@tanstack/vue-query";
import {
  persistQueryClientRestore,
  persistQueryClientSubscribe,
} from "@tanstack/query-persist-client-core";
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import App from "@/App.vue";
import { queryClient, QUERY_CACHE_STORAGE_KEY } from "@/queryClient";
import {
  recoverFromStaleChunks,
  STALE_CHUNK_RE,
} from "@/utils/recoverFromStaleChunks";
import router from "./router";

const app = createApp(App);

if (import.meta.env.VITE_SENTRY_FRONTEND_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_FRONTEND_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    enableLogs: true,

    tracesSampleRate: 0.1,

    // Stale-chunk import failures after a deploy are expected and recovered
    // from by recoverFromStaleChunks (reload once) — don't report them.
    ignoreErrors: [STALE_CHUNK_RE],
  });
}

app.use(VueQueryPlugin, { queryClient });

app.use(router);

recoverFromStaleChunks(router);

// Stale-while-revalidate across reloads: persist a subset of the query cache
// (the data the slow calendar pages depend on) to sessionStorage, restore it
// before mount so the previous result paints instantly, then let the normal
// query lifecycle refetch in the background. `buster` invalidates the cache
// when the shape changes; `maxAge` caps how long a cold copy is trusted.
//
// sessionStorage, not localStorage (Review 2.6): the cache holds member names,
// and should go when the tab does rather than sit on a shared reception PC.
// Clear what older builds left in localStorage, the refresh token included
// (it is an httpOnly cookie now).
try {
  window.localStorage.removeItem(QUERY_CACHE_STORAGE_KEY);
  window.localStorage.removeItem("refreshToken");
} catch {
  // Storage unavailable; nothing to clear.
}
const persister = createSyncStoragePersister({
  storage: window.sessionStorage,
  key: QUERY_CACHE_STORAGE_KEY,
  throttleTime: 1000,
});
const PERSISTED_QUERY_KEYS = new Set([
  "bookings",
  "pitches",
  "ef-units",
  "sites",
]);
const persistOptions = {
  queryClient,
  persister,
  maxAge: 1000 * 60 * 60 * 24,
  buster: "camc-cache-v1",
  dehydrateOptions: {
    shouldDehydrateQuery: (query: {
      queryKey: unknown;
      state: { status: string };
    }) =>
      query.state.status === "success" &&
      Array.isArray(query.queryKey) &&
      typeof query.queryKey[0] === "string" &&
      PERSISTED_QUERY_KEYS.has(query.queryKey[0]),
  },
};

async function boot() {
  try {
    await persistQueryClientRestore(persistOptions);
    persistQueryClientSubscribe(persistOptions);
  } catch {
    // Storage unavailable/corrupt/quota — carry on without persistence.
  }
  app.mount("#app");
}

boot();

export default app;
